import { useCallback, useEffect, useRef, useState } from 'react';

type UseInViewportOptions = {
  /**
   * Grows/shrinks the observed area. Pass a positive margin (e.g. `'600px 0px'`)
   * to flip `inView` before the element actually enters the viewport, so content
   * mounts ahead of scroll and there are no blank frames.
   */
  rootMargin?: string;
};

/**
 * Two-way `IntersectionObserver` wrapper: `inView` tracks the element's current
 * visibility and flips back to `false` once it leaves the (margin-expanded)
 * viewport. Use it to mount heavy tile content only near the viewport and
 * unmount it again when scrolled away, keeping the live DOM proportional to what
 * is on screen rather than to everything ever scrolled past.
 *
 * Unlike `react-intersection-observer`, the underlying observer is guaranteed to
 * disconnect on ref change and on unmount, so it never retains detached DOM. In
 * non-browser environments (no `IntersectionObserver`, e.g. jsdom) it resolves to
 * `inView === true` so dependent content still renders.
 */
export const useInViewport = <T extends Element = Element>({
  rootMargin,
}: UseInViewportOptions = {}) => {
  const [inView, setInView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const ref = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node) {
        return;
      }

      if (typeof IntersectionObserver === 'undefined') {
        setInView(true);
        return;
      }

      const observer = new IntersectionObserver(
        entries => {
          const entry = entries[entries.length - 1];
          if (entry) {
            setInView(entry.isIntersecting);
          }
        },
        { rootMargin }
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [rootMargin]
  );

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    },
    []
  );

  return { ref, inView };
};
